import React, { useState, useEffect } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../services/firebase';
import { User, Role, ROLE_LABELS } from '../types';

const ROLE_OPTIONS: Role[] = ['seller', 'supervisor', 'coordenador', 'gerente', 'gerente_nacional', 'pricing', 'diretor', 'admin'];

interface Props {
  seller: User | null; // null = novo vendedor
  onClose: () => void;
  onSaved: () => void;
}

export default function SellerFormModal({ seller, onClose, onSaved }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [codVend, setCodVend] = useState('');
  const [role, setRole] = useState<Role>('seller');
  const [password, setPassword] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [definindoSenha, setDefinindoSenha] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resetMsg, setResetMsg] = useState('');

  const isEdit = !!seller;

  // Define a senha diretamente (Admin SDK via Cloud Function)
  const handleDefinirSenha = async () => {
    if (!seller) return;
    setError('');
    setResetMsg('');
    if (novaSenha.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    setDefinindoSenha(true);
    try {
      const definir = httpsCallable(functions, 'definirSenhaVendedor');
      const resp: any = await definir({ uid: seller.id, novaSenha });
      setResetMsg(`Senha alterada com sucesso para ${resp.data?.email || seller.email}! Avise a nova senha ao vendedor.`);
      setNovaSenha('');
    } catch (e: any) {
      setError(e?.message || 'Não foi possível alterar a senha.');
    } finally {
      setDefinindoSenha(false);
    }
  };

  const handleResetPassword = async () => {
    if (!seller) return;
    setError('');
    setResetMsg('');
    try {
      await sendPasswordResetEmail(auth, seller.email);
      setResetMsg(`Link de redefinição enviado para ${seller.email}.`);
    } catch (e: any) {
      setError('Não foi possível enviar o e-mail de redefinição.');
    }
  };

  useEffect(() => {
    if (seller) {
      setName(seller.name || '');
      setEmail(seller.email || '');
      setPhone(seller.phone || '');
      setCodVend(seller.codVendSankhya ? String(seller.codVendSankhya) : '');
      setRole(seller.role || 'seller');
    }
  }, [seller]);

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) {
      setError('Preencha nome e e-mail.');
      return;
    }
    if (!isEdit && password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    setError('');
    setSaving(true);

    try {
      const codVendNum = codVend.trim() ? Number(codVend.trim()) : null;
      if (isEdit) {
        // Edição: atualiza nome, telefone e código do vendedor Sankhya
        await updateDoc(doc(db, 'users', seller!.id), {
          name: name.trim(),
          phone: phone.trim() || null,
          codVendSankhya: codVendNum,
          role,
        });
      } else {
        // Novo: criação NO SERVIDOR (Admin SDK). Antes era `createUserWithEmailAndPassword` no cliente,
        // o que obrigava o autocadastro a ficar ligado no Firebase Auth (qualquer um criava conta no
        // projeto) e exigia o truque do `secondaryAuth` para não deslogar o admin. Pelo servidor não
        // precisa de nenhum dos dois.
        await httpsCallable(functions, 'criarUsuario')({
          name: name.trim(),
          email: email.trim(),
          password,
          phone: phone.trim() || null,
          codVendSankhya: codVendNum,
          role,
        });
      }
      onSaved();
    } catch (e: any) {
      // A callable devolve HttpsError já com mensagem em português (e-mail duplicado/ inválido etc.).
      setError(e?.message || 'Não foi possível salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{isEdit ? 'Editar Usuário' : 'Novo Usuário'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>
          {error && <div style={styles.error}>{error}</div>}

          <label style={styles.label}>Nome *</label>
          <input style={styles.input} value={name} onChange={e => setName(e.target.value)} placeholder="Nome completo" />

          <label style={styles.label}>E-mail *</label>
          <input
            style={{ ...styles.input, ...(isEdit ? styles.inputDisabled : {}) }}
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="vendedor@empresa.com"
            disabled={isEdit}
          />
          {isEdit && <p style={styles.hint}>O e-mail de login não pode ser alterado.</p>}

          <label style={styles.label}>Telefone</label>
          <input style={styles.input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="(00) 00000-0000" />

          <label style={styles.label}>Perfil de acesso *</label>
          <select style={styles.input} value={role} onChange={e => setRole(e.target.value as Role)}>
            {ROLE_OPTIONS.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <p style={styles.hint}>
            {role === 'seller' ? 'Vê só a própria carteira.'
              : role === 'gerente' || role === 'coordenador' || role === 'supervisor' ? 'Vê apenas os vendedores da hierarquia dele (pelo CODVEND abaixo).'
              : 'Acesso total a todos os dados.'}
          </p>

          <label style={styles.label}>Código no Sankhya (CODVEND)</label>
          <input style={styles.input} value={codVend} onChange={e => setCodVend(e.target.value)} placeholder="Ex: 849" inputMode="numeric" />
          <p style={styles.hint}>
            {role === 'gerente' || role === 'coordenador' || role === 'supervisor'
              ? 'Código do gestor: o sistema mostra os vendedores cujo gerente/coordenador/supervisor é este código (após "Sincronizar hierarquia").'
              : 'Vendedor: clientes cadastrados por ele entram no Sankhya com este CODVEND.'}
          </p>

          {isEdit && (
            <div style={styles.resetBox}>
              <p style={styles.resetTitle}>Senha</p>
              {resetMsg && <div style={styles.success}>{resetMsg}</div>}

              <label style={styles.label}>Definir nova senha</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  style={{ ...styles.input, flex: 1 }}
                  type="text"
                  value={novaSenha}
                  onChange={e => setNovaSenha(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                />
                <button type="button" style={styles.definirBtn} onClick={handleDefinirSenha} disabled={definindoSenha}>
                  {definindoSenha ? 'Salvando...' : 'Definir'}
                </button>
              </div>
              <p style={styles.hint}>Define a senha na hora. Anote e repasse ao vendedor.</p>

              <button type="button" style={{ ...styles.resetBtn, marginTop: 8 }} onClick={handleResetPassword}>
                📧 Ou enviar link de redefinição por e-mail
              </button>
            </div>
          )}

          {!isEdit && (
            <>
              <label style={styles.label}>Senha de acesso *</label>
              <input style={styles.input} type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
              <p style={styles.hint}>O vendedor usará este e-mail e senha para entrar no app.</p>
            </>
          )}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancelar</button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, width: '90%', maxWidth: 440, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' },
  body: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4, marginTop: 12 },
  input: { width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' },
  inputDisabled: { background: '#f3f4f6', color: '#999' },
  hint: { fontSize: 12, color: '#888', margin: '4px 0 0' },
  error: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 14 },
  success: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 13 },
  resetBox: { marginTop: 16, padding: 14, background: '#f9fafb', borderRadius: 10, border: '1px solid #f0f0f0' },
  resetTitle: { fontSize: 12, fontWeight: 700, color: '#f5821f', textTransform: 'uppercase', margin: '0 0 8px' },
  resetBtn: { background: '#fff', color: '#f5821f', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13, width: '100%' },
  definirBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  cancelBtn: { background: '#f3f4f6', color: '#444', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  saveBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
};
